import { useState, forwardRef } from "react";
import { motion } from "framer-motion";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { CheckCircle, Loader2, Download, Globe } from "lucide-react";
import whitepaperMockup from "@/assets/whitepaper-mockup.png";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/i18n/LanguageContext";
import { getLeadMeta } from "@/lib/tracking";

type FormData = { name: string; email: string; company?: string; position?: string };

export const DownloadForm = forwardRef<HTMLDivElement>((_, ref) => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const { t } = useLanguage();

  const formSchema = z.object({
    name: z.string().min(2, t.download.validation.nameMin).max(100),
    email: z.string().email(t.download.validation.emailInvalid).max(255),
    company: z.string().max(100).optional(),
    position: z.string().max(100).optional(),
  });

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: { name: "", email: "", company: "", position: "" },
  });

  const onSubmit = async (data: FormData) => {
    setIsLoading(true);
    const { error } = await supabase.from("whitepaper_leads").insert({
      name: data.name,
      email: data.email,
      company: data.company || null,
      position: data.position || null,
      ...getLeadMeta(),
    });
    setIsLoading(false);
    if (error) {
      toast({ title: t.download.toast.errorTitle, description: t.download.toast.errorDescription, variant: "destructive" });
      return;
    }
    setIsSubmitted(true);
    toast({ title: t.download.toast.successTitle, description: t.download.toast.successDescription });
  };

  return (
    <section ref={ref} id="download" className="py-24 bg-background">
      <div className="container px-4">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="mb-8">
                <img src={whitepaperMockup} alt="Whitepaper" className="w-80 h-auto drop-shadow-2xl" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                <span className="font-serif italic">{t.download.title}</span>{t.download.titleSuffix}
              </h2>
              <p className="text-lg text-muted-foreground mb-6">{t.download.subtitle}</p>

              {t.download.whitepaperLanguageNote && (
                <div className="flex items-start gap-2 text-sm text-amber-700 dark:text-amber-400 mb-6 px-3 py-2.5 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded-lg">
                  <Globe className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span className="font-medium">{t.download.whitepaperLanguageNote}</span>
                </div>
              )}

              <ul className="space-y-3">
                {t.download.bulletPoints.map((item, idx) => (
                  <li key={idx} className="flex items-center gap-3 text-muted-foreground">
                    <CheckCircle className="h-5 w-5 text-report-success flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="bg-card rounded-2xl border border-border p-8 shadow-lg">
                {isSubmitted ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 rounded-full bg-report-success/10 flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="h-8 w-8 text-report-success" />
                    </div>
                    <h3 className="text-xl font-bold text-foreground mb-2">{t.download.success.title}</h3>
                    <p className="text-muted-foreground mb-6">{t.download.success.description}</p>
                    <Button asChild size="lg" className="bg-accent hover:bg-accent/90 text-accent-foreground">
                      <a href="/downloads/4-Tage-Woche-im-Bankenwesen.pdf" download>
                        <Download className="mr-2 h-5 w-5" />
                        {t.download.success.downloadButton}
                      </a>
                    </Button>
                    {t.download.whitepaperLanguageNote && (
                      <div className="flex items-center justify-center gap-2 text-sm text-amber-700 dark:text-amber-400 mt-4 px-3 py-2 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded-lg mx-auto max-w-xs">
                        <Globe className="h-4 w-4 flex-shrink-0" />
                        <span className="font-medium">{t.download.whitepaperLanguageNote}</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
                      <FormField control={form.control} name="name" render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t.download.formLabels.name}</FormLabel>
                          <FormControl><Input placeholder={t.download.formLabels.namePlaceholder} {...field} /></FormControl>
                          <FormMessage />
                        </FormItem>
                      )} />
                      <FormField control={form.control} name="email" render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t.download.formLabels.email}</FormLabel>
                          <FormControl><Input type="email" placeholder={t.download.formLabels.emailPlaceholder} {...field} /></FormControl>
                          <FormMessage />
                        </FormItem>
                      )} />
                      <FormField control={form.control} name="company" render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t.download.formLabels.company}</FormLabel>
                          <FormControl><Input placeholder={t.download.formLabels.companyPlaceholder} {...field} /></FormControl>
                          <FormMessage />
                        </FormItem>
                      )} />
                      <FormField control={form.control} name="position" render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t.download.formLabels.position}</FormLabel>
                          <FormControl><Input placeholder={t.download.formLabels.positionPlaceholder} {...field} /></FormControl>
                          <FormMessage />
                        </FormItem>
                      )} />
                      <Button type="submit" className="w-full bg-accent hover:bg-accent/90 text-accent-foreground py-6" disabled={isLoading}>
                        {isLoading ? (
                          <><Loader2 className="mr-2 h-5 w-5 animate-spin" />{t.download.formLabels.submitting}</>
                        ) : (
                          <><Download className="mr-2 h-5 w-5" />{t.download.formLabels.submit}</>
                        )}
                      </Button>
                      <p className="text-xs text-muted-foreground text-center">{t.download.formLabels.disclaimer}</p>
                    </form>
                  </Form>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
});

DownloadForm.displayName = "DownloadForm";
